import { enqueueSnackbar } from 'notistack';
import { Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  IconBan,
  IconEye,
  IconPlus,
  IconTrash,
  IconSearch,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import Select from '@/components/Select';
import ListSkeleton from '@/components/SkeletonLoader/ListSkeleton';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { EBlockUnblock, ELessonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getClassDropdown } from '@/service/classService';
import { removeStudent, getStudentList } from '@/service/studentService';
import { blockUnblockUser } from '@/service/teacherService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { IClassDropdownItem } from '@/types';
import { getAssetUrl, formatLastActive } from '@/utils';

import { AddStudentModal } from '../classDetails/components/AddStudentModal';
import { StudentDetailDrawer } from '../classDetails/components/StudentDetailDrawer';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface IStudent {
  id: string;
  name: string;
  email: string;
  class_name?: string;
  class_teacher_name: string | null;
  lessons_count: number;
  status: ELessonStatus;
  last_update: number | null;
  profile_pic?: string | null;
  avg_completion: number;
  teacher_profile_pic?: string | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

const Students = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();

  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();

  // Form for search
  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const searchValue = watch('search');
  const debouncedSearch = useDebounce(searchValue, 500);

  // States
  const [students, setStudents] = useState<IStudent[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [classFilter, setClassFilter] = useState<string[]>([]);
  const [classOptions, setClassOptions] = useState<IClassDropdownItem[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC';
  } | null>(null);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { key, direction: 'ASC' };
    });
  };

  const classFilterKey = classFilter.join(',');
  const { settings } = useSettingsStore();

  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<IStudent | null>(null);
  const [isStudentDrawerOpen, setIsStudentDrawerOpen] = useState(false);

  useEffect(() => {
    setTitle(formatText('students.title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // ── Fetch class dropdown ─────────────────────────────────────────────────

  useEffect(() => {
    const fetchClassDropdown = async () => {
      const res = await getClassDropdown();
      if (res?.data) {
        setClassOptions(res.data ?? []);
      }
    };
    fetchClassDropdown();
  }, []);

  // ── Reset page on search change ──────────────────────────────────────────

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, classFilterKey, sortConfig]);

  // ── Fetch students ───────────────────────────────────────────────────────

  const fetchStudents = async () => {
    if (students.length === 0 && !debouncedSearch && classFilter.length === 0) {
      setScreenLoading(true);
    } else {
      setTableLoading(true);
    }

    try {
      const query: Record<string, unknown> = {
        limit: 10,
        offset: (currentPage - 1) * 10,
      };
      if (debouncedSearch) query.search = debouncedSearch;
      if (classFilter.length > 0) query.class_id = classFilter;

      if (sortConfig?.key && sortConfig?.direction) {
        query[`order[${sortConfig.key}]`] = sortConfig.direction;
      } else {
        query[`order[created_at]`] = 'DESC';
      }

      const res = await getStudentList(query);
      if (res?.data) {
        setStudents(res.data ?? []);
        setTotal(res.total ?? res.data.length ?? 0);
      }
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, classFilterKey, sortConfig]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleViewStudent = (student: IStudent) => {
    setSelectedStudent(student);
    setIsStudentDrawerOpen(true);
  };

  const handleBlockStudent = (student: IStudent) => {
    setSelectedStudent(student);
    setIsBlockModalOpen(true);
  };

  const handleRemoveStudent = (student: IStudent) => {
    setSelectedStudent(student);
    setIsRemoveModalOpen(true);
  };

  const confirmBlockUnblockStudent = async () => {
    if (!selectedStudent) return;
    const isCurrentlyInactive =
      selectedStudent.status === ELessonStatus.IN_ACTIVE;

    try {
      const response = await blockUnblockUser(
        selectedStudent.id,
        EBlockUnblock.STUDENT,
        { is_block: !isCurrentlyInactive }
      );

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setIsBlockModalOpen(false);
        setSelectedStudent(null);
        fetchStudents();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const confirmRemoveStudent = async () => {
    if (!selectedStudent) return;

    try {
      const response = await removeStudent(selectedStudent.id);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        setIsRemoveModalOpen(false);
        setSelectedStudent(null);
        fetchStudents();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────

  const columns = [
    {
      key: 'name',
      title: 'students.table.student-name',
      dataIndex: 'name',
      width: '193px',
      sorter: true,
      handleSort: () => handleSort('name'),
      render: (record: IStudent) => (
        <div className="flex items-center gap-3">
          <UserAvatarList
            user={record.name}
            image={getAssetUrl(record?.profile_pic)}
            className="w-10 h-10 text-sm"
          />
          <span className="text-neutral-primary text-sm font-medium">
            {record.name}
          </span>
        </div>
      ),
    },
    {
      key: 'email',
      title: 'students.table.email-id',
      dataIndex: 'email',
      width: '268px',
      sorter: true,
      handleSort: () => handleSort('email'),
      render: (record: IStudent) => (
        <span className="text-neutral-secondary text-sm">{record.email}</span>
      ),
    },
    {
      key: 'class_name',
      title: 'students.table.assigned-class',
      dataIndex: 'class_name',
      width: '160px',
      sorter: true,
      handleSort: () => handleSort('class_name'),
      render: (record: IStudent) => (
        <span className="text-neutral-primary text-sm font-medium">
          {record.class_name ?? '-'}
        </span>
      ),
    },
    {
      key: 'class_teacher_name',
      title: 'students.table.teacher-name',
      dataIndex: 'class_teacher_name',
      width: '224px',
      sorter: true,
      handleSort: () => handleSort('class_teacher_name'),
      render: (record: IStudent) =>
        record.class_teacher_name ? (
          <div className="flex items-center gap-3">
            <UserAvatarList
              user={record.class_teacher_name}
              image={getAssetUrl(record?.teacher_profile_pic)}
              className="w-10 h-10 text-sm"
            />
            <span className="text-neutral-primary text-sm">
              {record.class_teacher_name}
            </span>
          </div>
        ) : (
          <span className="text-neutral-secondary text-sm">-</span>
        ),
    },
    {
      key: 'lessons_count',
      title: 'students.table.lessons-created',
      dataIndex: 'lessons_count',
      width: '159px',
      sorter: true,
      handleSort: () => handleSort('lessons_count'),
      render: (record: IStudent) => (
        <div className="flex items-center gap-2 px-2 py-0.5 w-fit rounded-full bg-pink-50">
          <span className="text-[#DB2777] text-sm font-medium">
            {record.lessons_count}
          </span>
        </div>
      ),
    },
    {
      key: 'avg_completion',
      title: 'students.table.avg-completion',
      dataIndex: 'avg_completion',
      width: '275px',
      sorter: true,
      handleSort: () => handleSort('avg_completion'),
      render: (record: IStudent) => (
        <div className="flex items-center gap-3 w-full">
          <ProgressBar value={record.avg_completion ?? 0} className="flex-1" />
        </div>
      ),
    },
    {
      key: 'status',
      title: 'students.table.status',
      dataIndex: 'status',
      width: '103.5px',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (record: IStudent) => {
        const config = LessonStatusConfig[record.status as ELessonStatus];
        return config ? (
          <TagCapsule
            value={formatText(config.label)}
            className={config.className}
          />
        ) : (
          <span>-</span>
        );
      },
    },
    {
      key: 'last_update',
      title: 'students.table.last-update',
      dataIndex: 'last_update',
      width: '103.5px',
      sorter: true,
      handleSort: () => handleSort('last_update'),
      render: (record: IStudent) => (
        <span className="text-neutral-secondary text-sm">
          {record.last_update
            ? formatLastActive(record.last_update, formatText)
            : '-'}
        </span>
      ),
    },
    {
      key: 'action',
      title: 'common.actions.action',
      dataIndex: 'action',
      width: '71px',
      render: (record: IStudent) => {
        const isInactive = record.status === ELessonStatus.IN_ACTIVE;
        return (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'common.actions.view',
                icon: <IconEye size={18} />,
                onClick: () => handleViewStudent(record),
              },
              {
                key: 'block',
                label: isInactive
                  ? 'common.actions.unblock'
                  : 'common.actions.block',
                icon: <IconBan size={18} stroke={1.5} />,
                isDanger: !isInactive,
                onClick: () => handleBlockStudent(record),
              },
              {
                key: 'remove',
                label: 'common.actions.remove',
                icon: <IconTrash size={18} stroke={1.5} />,
                isDanger: true,
                onClick: () => handleRemoveStudent(record),
              },
            ]}
          />
        );
      },
    },
  ];

  // ── Empty state checks ────────────────────────────────────────────────────

  const hasNoStudents =
    !isScreenLoading &&
    students.length === 0 &&
    !debouncedSearch &&
    classFilter.length === 0;
  const isEmptySearch =
    !isScreenLoading &&
    students.length === 0 &&
    (!!debouncedSearch || classFilter.length > 0);

  if (isScreenLoading) {
    return <ListSkeleton columns={columns} />;
  }

  if (hasNoStudents && !isEmptySearch && !isScreenLoading) {
    return (
      <>
        <EmptyState
          imageSrc="/not-found.svg"
          title={formatText('students.empty-title')}
          description={formatText('students.empty-description')}
          actions={
            <div className="flex items-center gap-3">
              <Button
                label={formatText('students.actions.add-students')}
                variant="primary"
                icon={<IconPlus size={18} />}
                className="h-12 px-4 cursor-pointer"
                onClick={() => setIsAddStudentModalOpen(true)}
              />
            </div>
          }
        />
        <Activity mode={isAddStudentModalOpen ? 'visible' : 'hidden'}>
          <AddStudentModal
            isOpen={isAddStudentModalOpen}
            onClose={() => setIsAddStudentModalOpen(false)}
            onSuccess={() => {
              fetchStudents();
            }}
          />
        </Activity>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="students-page-container">
      {/* Header with search and filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 ps-1">
          <Input
            name="search"
            register={register}
            placeholder={formatText('students.search-placeholder')}
            leftIcon={{
              icon: <IconSearch size={18} className="text-neutral-400" />,
            }}
            className="min-w-75"
            inputClassName="py-[13px]"
            dataTestId="student-search-input"
          />
          <Select
            options={classOptions}
            selected={classFilter}
            onChange={(option: string | string[]) =>
              setClassFilter(Array.isArray(option) ? option : [])
            }
            placeholder={formatText('students.class-filter')}
            isClearable
            isMulti
            className="min-w-37.5! py-3.25"
            dataTestId="class-filter-select"
          />
        </div>

        <div className="flex items-center gap-3">
          <Button
            label={formatText('students.actions.add-students')}
            variant="primary"
            icon={<IconPlus size={18} />}
            className="h-12 px-4 cursor-pointer"
            onClick={() => setIsAddStudentModalOpen(true)}
            dataTestId="add-student-button"
          />
        </div>
      </div>

      {/* Student Overview Table */}
      <div className="flex flex-col gap-7" data-testid="students-table-section">
        <h2 className="text-xl font-bold text-neutral-primary">
          {formatText('students.overview-title')}
        </h2>

        <CustomTable
          dataSource={students}
          columns={columns}
          rowKey="id"
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={10}
          isEmptySearch={isEmptySearch}
          isLoading={isTableLoading}
          fieldName={sortConfig?.key}
          sortDirection={sortConfig?.direction}
          dataTestId="students-table"
          onRowClick={(id: string) => {
            const student = students.find((s) => s?.id === id);
            if (student) handleViewStudent(student);
          }}
        />
      </div>

      {/* Add Student Modal */}
      <Activity mode={isAddStudentModalOpen ? 'visible' : 'hidden'}>
        <AddStudentModal
          isOpen={isAddStudentModalOpen}
          onClose={() => setIsAddStudentModalOpen(false)}
          onSuccess={() => {
            fetchStudents();
          }}
        />
      </Activity>

      {/* Student Detail Drawer */}
      <Activity mode={isStudentDrawerOpen ? 'visible' : 'hidden'}>
        <StudentDetailDrawer
          isOpen={isStudentDrawerOpen}
          onClose={() => setIsStudentDrawerOpen(false)}
          student={selectedStudent}
        />
      </Activity>

      {/* Block Confirmation Modal */}
      <Activity mode={isBlockModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isBlockModalOpen}
          setIsOpen={setIsBlockModalOpen}
          title={
            selectedStudent?.status === ELessonStatus.IN_ACTIVE
              ? formatText('students.modals.unblock.title')
              : formatText('students.modals.block.title')
          }
          descriptionPrefix={
            selectedStudent?.status === ELessonStatus.IN_ACTIVE
              ? formatText('common.confirmation.unblock-prefix')
              : formatText('common.confirmation.block-prefix')
          }
          descriptionSuffix={
            selectedStudent?.status !== ELessonStatus.IN_ACTIVE
              ? 'students.modals.block.suffix'
              : undefined
          }
          entityName={selectedStudent?.name}
          confirmLabel={
            selectedStudent?.status === ELessonStatus.IN_ACTIVE
              ? formatText('students.modals.unblock.confirm')
              : formatText('students.modals.block.confirm')
          }
          onConfirm={confirmBlockUnblockStudent}
          isDestructive={selectedStudent?.status !== ELessonStatus.IN_ACTIVE}
        />
      </Activity>

      {/* Remove Confirmation Modal */}
      <Activity mode={isRemoveModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveModalOpen}
          setIsOpen={setIsRemoveModalOpen}
          title={formatText('students.modals.remove.title')}
          descriptionPrefix={formatText('common.confirmation.remove-prefix')}
          descriptionSuffix="students.modals.remove.suffix"
          entityName={selectedStudent?.name}
          confirmLabel={formatText('students.modals.remove.confirm')}
          onConfirm={confirmRemoveStudent}
          isDestructive
        />
      </Activity>
    </div>
  );
};

export default Students;
